<template>
  <router-link
    class="link"
    :to="{
      name: `${SINGLE_PORTFOLIO.slug}__${getCurrentLanguage}`,
      params: { slug: caseStudy.slug },
    }"
  >
    <div
      v-show="!isMobile"
      class="case-study"
      @mouseenter="playVid(caseStudy.id)"
      @mouseleave="mouseLeave($event, caseStudy.id)"
      @mousemove="throttleHandler"
    >
      <div class="content">
        <h2 class="title">{{ caseStudy.clientName }}</h2>
        <p class="subtitle">{{ caseStudy.description }}</p>
        <div class="icon-container">
          <ArrowLinearIcon />
        </div>
      </div>
      <div class="img-wrapper">
        <div class="img-container">
          <client-only>
            <video
              :id="`player_${caseStudy.id}`"
              v-lazy-load :data-src="caseStudy.video.mediaItemUrl"
              type="video/mp4"
              height="600"
              class="player"
              :class="{ '--hide': !videoPlaying }"
              muted
              loop
            ></video>
          </client-only>
          <AppImage
            :image="caseStudy.caseStudiesBoxImage.srcSet"
            :alt="caseStudy.caseStudiesBoxImage.altText"
            :sizes="caseStudy.caseStudiesBoxImage.sizes"
            :blurred-preview="caseStudy.caseStudiesBoxImage.imageBlur"
            :class="{ '--hide': videoPlaying }"
            class="img"
          />
        </div>
      </div>
    </div>
    <div v-show="isMobile" class="case-study">
      <div class="img-wrapper">
        <div class="img-container">
          <AppImage
            :image="caseStudy.caseStudiesBoxImage.srcSet"
            :alt="caseStudy.caseStudiesBoxImage.altText"
            :sizes="caseStudy.caseStudiesBoxImage.sizes"
            :blurred-preview="caseStudy.caseStudiesBoxImage.imageBlur"
            class="img"
          />
        </div>
      </div>
      <div class="content">
        <h2 class="title">{{ caseStudy.clientName }}</h2>
        <p class="subtitle">{{ caseStudy.description }}</p>
        <div class="icon-container">
          <ArrowLinearIcon />
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import { mapGetters } from 'vuex'
import throttle from 'lodash/throttle'
import { isMobile } from '@/utils/mobile-device-detect.js'
import { eventPath } from '@/utils/event-path.js'
import ArrowLinearIcon from '@/components/icons/ArrowLinearIcon'
import { ROUTES } from '@/utils/routes'
import AppImage from '@/components/common/AppImage'

export default {
  name: 'CaseStudiesItem',
  components: {
    ArrowLinearIcon,
    AppImage,
  },
  props: {
    caseStudy: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      constrain: 600,
      mouseleaveTimeout: null,
      SINGLE_PORTFOLIO: ROUTES.SINGLE_PORTFOLIO,
      isMobile,
      videoPlaying: false,
      throttleHandler: undefined,
    }
  },
  computed: {
    ...mapGetters(['getCurrentLanguage']),
    currentLanguageForRouter() {
      if (this.getCurrentLanguage === 'en') return null
      return this.getCurrentLanguage
    },
  },
  mounted() {
    window.addEventListener('resize', this.resize)
  },
  created() {
    this.throttleHandler = throttle((event) => {
      this.mouseMove(event)
    }, 10)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
  },
  methods: {
    resize() {
      this.isMobile = isMobile
    },
    async playVid(id) {
      if (isMobile) return
      const player = document.querySelector(`#player_${id}`)

      await player?.play()
      if (!player?.paused) this.videoPlaying = true
    },
    stopVid(id) {
      const player = document.querySelector(`#player_${id}`)
      if (!player?.paused && this.videoPlaying) {
        player?.pause()
        this.videoPlaying = false
      }
    },
    transforms(x, y, el) {
      const box = el.getBoundingClientRect()
      const calcX = -(y - box.y - box.height / 3) / this.constrain
      const calcY = (x - box.x - box.width / 3) / this.constrain

      return `perspective(84px) rotateX(${calcX}deg) rotateY(${calcY}deg)`
    },
    transformElement(el, xyEl) {
      el.style.transform = this.transforms.apply(null, xyEl)
    },
    resetTransform(el) {
      if (el) {
        window.requestAnimationFrame(() => {
          el.style.transform = `perspective(500px) rotateX(0) rotateY(0) scale(1)`
        })
      }
    },
    mouseMove(e) {
      const elPath = eventPath(e)
      const el = elPath.find((item) => {
        return item.classList.contains('case-study')
      })
      if (!el) return
      const elToTransform = el?.querySelector('.img-wrapper')

      if (this.activeItem !== el) this.resetTransform(this.activeItem)
      this.activeItem = el

      const xy = [e.clientX, e.clientY]
      const position = xy.concat(el)

      window.requestAnimationFrame(() => {
        this.transformElement(elToTransform, position)
      })
    },
    mouseLeave(e, id) {
      const elPath = eventPath(e)

      const el = elPath.find((item) => {
        return item.classList.contains('case-study')
      })
      if (!el) return

      const elChildren = el.querySelector('.img-wrapper')

      this.stopVid(id)

      if (this.mouseleaveTimeout) clearTimeout(this.mouseleaveTimeout)

      this.mouseleaveTimeout = setTimeout(() => {
        this.resetTransform(elChildren)
      }, 200)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/components/case-studies/CaseStudiesItem';
</style>
